import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from './comps';
import { FormikProvider, useFormik } from 'formik';
import { Poppins, Spacer } from 'src/common';
import { queryCache } from 'react-query';
import { Guard } from './Guard';
import { getDiff } from 'src/utils/getDiff';
import IncidentForm from '../formComposed/IncidentForm';
import { incidentInitialValues, incidentValidationSchema } from '../formComposed/IncidentForm/util';
import { selectUserOption } from 'src/utils/misc';
import { useAuth } from 'src/state/auth';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { IncidentInput, useUpsertIncidentsMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';

const StyledModal = styled(Modal)`
  width: 950px;
  min-height: 200px;
  padding: 0;
  max-height: calc(100% - 40px);
  overflow-y: auto;

  .grid {
    display: grid;
    grid-gap: 20px;

    &--2 {
      grid-template-columns: 1fr 1fr;
    }
  }

  .error {
    color: ${({ theme }) => theme.colors.error};
    font-size: 14px;
    text-align: center;
  }

  ${({ theme }) => theme.breakpoints.down('md')} {
    .grid {
      grid-gap: 10px;
    }
  }
  ${({ theme }) => theme.breakpoints.down('sm')} {
    .grid {
      &--2 {
        grid-template-columns: 1fr;
      }
    }
  }
`;

interface IncidentModalProps {
  onClose: () => void;
  initialData?: IncidentInput | null;
}

export const IncidentModal: React.FC<IncidentModalProps> = ({ onClose, initialData }) => {
  const [responseErr, setResponseErr] = useState('');
  const [isCloseRequested, setIsCloseRequested] = useState(false);
  const { usersOptios, workspaceId } = useAuth();
  const { refetchQuery } = useCache();

  const onSuccess = () => {
    queryCache.invalidateQueries();
    refetchQuery({ include: ['incidents', 'incident'] });
    onClose();
  };
  const onError = (err: any) => setResponseErr(err.message || 'Something went wrong');

  const [upsertIncidents] = useUpsertIncidentsMutation({
    onCompleted: onSuccess,
    onError,
  });

  const initValues: IncidentInput = initialData ? initialData : incidentInitialValues;

  const formik = useFormik({
    initialValues: initValues,
    validationSchema: incidentValidationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async (values) => {
      const { userAdd, userUpdate } = selectUserOption({
        usersOptios,
        selectedUser: values.owner,
        initialUser: initValues.owner,
      });

      if (initialData) {
        const diff = getDiff(initValues, values);
        await upsertIncidents({
          variables: {
            incidentsData: [{ id: initialData.id, ...diff, owner: userUpdate }],
            workspaceId,
          },
        });
      } else {
        await upsertIncidents({
          variables: {
            incidentsData: [{ ...values, owner: userAdd }],
            workspaceId,
          },
        });
      }
    },
  });

  return (
    <Guard
      onCancel={() => setIsCloseRequested(false)}
      onClose={onClose}
      isOpen={isCloseRequested}
      isDirty={formik.dirty}
      modalComponent={
        <StyledModal onRequestClose={() => setIsCloseRequested(true)} isOpen>
          <Spacer $px={30} />
          <div className="h-padding">
            <Poppins className="m-title" px={28}>
              {initialData ? 'Update' : 'Add'} Incident
            </Poppins>
            <Spacer $px={20} />

            <FormikProvider value={formik}>
              <IncidentForm
                buttonTitle={initialData ? 'UPDATE' : '+ ADD'}
                onSubmitCb={() =>
                  mpEvent(MPEvents.ButtonClick, {
                    button: initialData ? 'Update' : 'Add',
                    modal: 'Incident modal',
                    tags: ['INCIDENT'],
                  })
                }
              />
            </FormikProvider>

            {!!responseErr && (
              <>
                <Spacer $px={15} />
                <div className="error">{responseErr}</div>
              </>
            )}
          </div>
          <Spacer $px={30} />
        </StyledModal>
      }
    />
  );
};
